import React, { useCallback, useState, useRef, useEffect, useMemo, useContext } from 'react';
import { Alert, Pressable, Text, View, Platform, KeyboardAvoidingView, Keyboard, useWindowDimensions } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { MotiView } from 'moti';
import { ToolbarContext } from '@hooks/toolbarContext';
import DomEditor, { DomEditorHandle } from '@navigation/screens/LowLevel/Components/DomEditor';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { XStack, YStack } from 'tamagui';
import apis from '@api/api';

interface ModuleItem {
  _id: string;
  title: string;
  description?: string;
  content?: string;
  order?: number;
  duration?: string | number;
  type: 'video' | 'text' | 'quiz' | 'assignment' | 'primary';
  completed?: boolean;
  level?: string;
  resources?: Array<{
    _id: string;
    title: string;
    url: string;
    type: 'file' | 'link' | 'video';
  }>;
  connections?: { targetId: string; type: string }[];
  [key: string]: any;
}

interface ModuleEditorProps {
  isVisible: boolean;
  module: ModuleItem | null;
  onClose: () => void;
  onSave: (moduleId: string, updatedModule: Partial<ModuleItem>) => void;
}

const ModuleEditor: React.FC<ModuleEditorProps> = ({ isVisible, module, onClose, onSave }) => {
  const editorRef = useRef<DomEditorHandle>(null);
  const { setEditorContext } = useContext(ToolbarContext);
  const theme = themeSelector();
  const { height, width } = useWindowDimensions();

  // Editor state
  const [editorState, setEditorState] = useState(null);
  const [showSettings, setShowSettings] = useState(true);

  // Performance optimization states
  const [cachedContent, setCachedContent] = useState<string>('');
  const [lastContentUpdate, setLastContentUpdate] = useState<number>(0);
  const contentCacheRef = useRef<string>('');
  const contentUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Theme-based colors for editor
  const editorColors = useMemo(() => ({
    background: theme ? '#ffffff' : '#1a1a1a',
    surface: theme ? '#f8f9fa' : '#2d2d2d',
    border: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    text: theme ? '#333333' : '#e0e0e0',
    accent: theme ? '#7c3aed' : '#a78bfa',
    toolbar: theme ? '#ffffff' : '#252525',
    toolbarBorder: theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
    shadow: theme
      ? '0 2px 8px rgba(0, 0, 0, 0.1)'
      : '0 2px 8px rgba(0, 0, 0, 0.3)',
  }), [theme]);

  // Get module type icon and color
  const getModuleTypeInfo = useCallback((type: string) => {
    switch (type) {
      case 'video':
        return {
          icon: 'play-circle',
          color: theme ? '#ef4444' : '#f87171',
          bgColor: theme ? '#fef2f2' : '#7f1d1d'
        };
      case 'text':
        return {
          icon: 'text-box',
          color: theme ? '#3b82f6' : '#60a5fa',
          bgColor: theme ? '#eff6ff' : '#1e3a8a'
        };
      case 'quiz':
        return {
          icon: 'help-circle',
          color: theme ? '#f59e0b' : '#fbbf24',
          bgColor: theme ? '#fffbeb' : '#92400e'
        };
      case 'assignment':
        return {
          icon: 'clipboard-check',
          color: theme ? '#10b981' : '#34d399',
          bgColor: theme ? '#f0fdf4' : '#064e3b'
        };
      default:
        return {
          icon: 'book',
          color: theme ? '#6b7280' : '#9ca3af',
          bgColor: theme ? '#f9fafb' : '#1f2937'
        };
    }
  }, [theme]);

  // Handle editor changes
  const handleEditorChange = useCallback(async (item) => {
    setEditorState(item);
  }, []);

  // Save module content (handled by DomEditor's saveFn)
  const handleSave = useCallback(async (content: string) => {
    if (!module) return;

    try {
      const updatedModule = {
        ...module,
        content: content,
        description: content.replace(/<[^>]*>/g, '').substring(0, 200) + '...' // Extract plain text description
      };


      onSave(module._id, updatedModule);

      // Close the editor after successful save
      onClose();
    } catch (error) {
      console.error('Error saving module:', error);
      Alert.alert('Error', 'Failed to save module content');
    }
  }, [module, onSave, onClose]);

  // Handle AI request
  const handleRequestAI = useCallback(async (data: {
    prompt: string;
    url?: string;
  }) => {
    if (!editorRef.current) return;

    try {
      const res = await apis.genKnowledge(data);
      if (res?.data?.content) {
        editorRef.current.setContent(res.data.content);
      }
    } catch (error) {
      console.error('AI generation error:', error);
      Alert.alert('Error', 'Failed to generate content with AI');
    }
  }, []);

  // Optimized content getter with caching
  const getCachedContent = useCallback(() => {
    const now = Date.now();
    if (now - lastContentUpdate < 500 && contentCacheRef.current) {
      return contentCacheRef.current;
    }

    try {
      const content = editorRef.current?.getContent() || '';
      contentCacheRef.current = content;
      setLastContentUpdate(now);
      return content;
    } catch (e) {
      console.error('Get content error:', e);
      return contentCacheRef.current || '';
    }
  }, [lastContentUpdate]);

  // Set up editor context when visible
  useEffect(() => {
    if (!isVisible || !editorRef.current || !module) {
      setEditorContext(null);
      return;
    }

    // Create stable callback functions
    const stableInsertContent = (content: string) => {
      try {
        const current = editorRef.current?.getContent() || '';
        const sep = current.trim() ? '\n\n' : '';
        const newContent = current + sep + content;
        editorRef.current?.setContent(newContent);
        contentCacheRef.current = newContent;
        setLastContentUpdate(Date.now());
      } catch (e) {
        console.error('Insert content error:', e);
      }
    };

    const stableSetContent = (content: string) => {
      try {
        editorRef.current?.setContent(content);
        contentCacheRef.current = content;
        setLastContentUpdate(Date.now());
      } catch (e) {
        console.error('Set content error:', e);
      }
    };

    const stableGetContent = () => {
      return getCachedContent();
    };

    setEditorContext({
      ref: editorRef,
      isConnected: true,
      contentWidth: width,
      contentHeight: height,
      insertContent: stableInsertContent,
      setContent: stableSetContent,
      getContent: stableGetContent,
      appendContent: stableInsertContent,
      prependContent: (content: string) => {
        try {
          const current = editorRef.current?.getContent() || '';
          const newContent = content + current;
          editorRef.current?.setContent(newContent);
          contentCacheRef.current = newContent;
          setLastContentUpdate(Date.now());
        } catch (e) {
          console.error('Prepend content error:', e);
        }
      },
      clearContent: () => {
        try {
          editorRef.current?.setContent('');
          contentCacheRef.current = '';
          setLastContentUpdate(Date.now());
        } catch (e) {
          console.error('Clear content error:', e);
        }
      }
    });

    // Set up content monitoring
    const interval = setInterval(() => {
      try {
        const content = editorRef.current?.getContent() || '';
        if (content !== contentCacheRef.current) {
          contentCacheRef.current = content;
          setLastContentUpdate(Date.now());
          setCachedContent(content);
        }
      } catch (e) {
        console.error('Content monitoring error:', e);
      }
    }, 1000);

    return () => {
      setEditorContext(null);
      clearInterval(interval);
      if (contentUpdateTimeoutRef.current) {
        clearTimeout(contentUpdateTimeoutRef.current);
      }
    };
  }, [isVisible, module, setEditorContext, width, height, getCachedContent]);

  // Handle keyboard events
  useEffect(() => {
    const keyboardListener = Keyboard.addListener('keyboardDidShow', () => {
      setShowSettings(false);
    });

    return () => {
      keyboardListener.remove();
    };
  }, []);


  if (!isVisible || !module) return null;

  const moduleTypeInfo = getModuleTypeInfo(module.type);

  return (
    <>
      {/* Backdrop */}
      <MotiView
        from={{ opacity: 0 }}
        animate={{ opacity: isVisible ? 0.5 : 0 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'black',
          zIndex: 1000,
        }}
        onTouchEnd={onClose}
      />

      {/* Editor Panel */}
      <MotiView
        from={{
          translateX: -width,
          opacity: 0
        }}
        animate={{
          translateX: isVisible ? 0 : -width,
          opacity: isVisible ? 1 : 0
        }}
        exit={{
          translateX: -width,
          opacity: 0
        }}
        transition={{
          type: 'timing',
          duration: 300,
        }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          marginTop: "2%",
          width: width * 0.4, // 40% of screen width
          height: '100%',
          paddingBottom: "2%",
          backgroundColor: editorColors.background,
          zIndex: 1001,
          ...Platform.select({
            web: {
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)',
            },
            default: {
              shadowColor: '#000',
              shadowOffset: { width: -2, height: 0 },
              shadowOpacity: 0.3,
              shadowRadius: 10,
              elevation: 10,
            }
          })
        }}
      >
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          {/* Header */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: 16,
            paddingVertical: 12,
            backgroundColor: editorColors.surface,
            borderBottomWidth: 1,
            borderBottomColor: editorColors.border,
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
              <MaterialCommunityIcons
                name={moduleTypeInfo.icon as any}
                size={24}
                color={moduleTypeInfo.color}
                style={{ marginRight: 12 }}
              />
              <View style={{ flex: 1 }}>
                <Text style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: editorColors.text,
                }} numberOfLines={1}>
                  {module.title}
                </Text>
                <Text style={{
                  fontSize: 12,
                  color: editorColors.text,
                  opacity: 0.7,
                }}>
                  {module?.type?.toUpperCase()} • {typeof module.duration === 'number' ? module.duration : module.duration || '0'} min
                </Text>
              </View>
            </View>

            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Pressable
                onPress={onClose}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: editorColors.border,
                }}
              >
                <MaterialCommunityIcons
                  name="close"
                  size={20}
                  color={editorColors.text}
                />
              </Pressable>
            </View>
          </View>

          {/* Editor Content */}
          <View style={{ flex: 1, padding: 16 }}>
            <DomEditor
              ref={editorRef}
              saveFn={handleSave}
              backFn={onClose}
              initialContent={module.content || ''}
              editorColors={editorColors}
            />
          </View>

        </KeyboardAvoidingView>
      </MotiView>
    </>
  );
};

export default ModuleEditor;
